import { useState, useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { DEFAULT_PAGE_SIZE } from '../constant';
import { resetSearch } from '../../Store/SearchReducer/action';

export const usePaginatedSearch = ({
  entity,
  searchState,
  getInitialState = () => ({}),
  preserveSearch,
  isReady,
  buildParams,     // (overrideSearch?) => params
  executeSearch,
  searchAction,
}) => {
  const dispatch = useDispatch();
  const [paginationState, setPaginationState] = useState({
    page: 0,
    size: DEFAULT_PAGE_SIZE,
  });

  useEffect(() => {
    if (!isReady) return;

    // KEY: check if searchState has real data
    // Hard refresh wipes Redux → empty → use initial even if preserveSearch=true
    const isSearchStateEmpty =
      !searchState || Object.keys(searchState).length === 0;

    const initialSearch =
      preserveSearch && !isSearchStateEmpty
        ? searchState
        : getInitialState();

    if (!preserveSearch || isSearchStateEmpty) {
      dispatch(resetSearch(entity, initialSearch));
    }

    setPaginationState({ page: 0, size: DEFAULT_PAGE_SIZE });

    const params = buildParams(initialSearch);
    if (!params) return;

    executeSearch(searchAction, {
      ...params,
      page: 0,
      size: DEFAULT_PAGE_SIZE,
    });
  }, [isReady]);

  const search = useCallback(
    async (overridePage, overrideSearch) => {
      const params = buildParams(overrideSearch);
      if (!params) return;

      const pageToUse = overridePage !== undefined ? overridePage : paginationState.page;

      if (overridePage !== undefined) {
        setPaginationState((prev) => ({ ...prev, page: overridePage }));
      }

      await executeSearch(searchAction, {
        ...params,
        page: pageToUse,
        size: paginationState.size,
      });
    },
    [buildParams, executeSearch, searchAction, paginationState.page, paginationState.size]
  );

  const changePage = useCallback(
    async (newPage) => {
      setPaginationState((prev) => ({ ...prev, page: newPage }));
      const params = buildParams();
      if (!params) return;

      await executeSearch(searchAction, {
        ...params,
        page: newPage,
        size: paginationState.size,
      });
    },
    [buildParams, executeSearch, searchAction, paginationState.size]
  );

  const changePageSize = useCallback(
    async (newSize) => {
      setPaginationState({ page: 0, size: newSize });
      const params = buildParams();
      if (!params) return;

      await executeSearch(searchAction, {
        ...params,
        page: 0,
        size: newSize,
      });
    },
    [buildParams, executeSearch, searchAction]
  );

  return {
    paginationState,
    search,
    changePage,
    changePageSize,
  };
};